import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import DashboardLayout from "../../components/DashboardLayout";
import FormModal from "../../components/FormModal";
import { Plus, Edit, Trash2, Image as ImageIcon } from "lucide-react";
import { useForm } from "@inertiajs/react";

interface Client {
  id: number;
  name: string;
  description: string;
  image_url?: string;
}

interface ClientsProps {
  clients: Client[];
  store_logo?: string;
  store_name?: string;
  auth: {
    admin: {
      email: string;
    };
  };
}

const Clients = ({ clients, store_logo, store_name }: ClientsProps) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const {
    data,
    setData,
    post,
    delete: destroy,
    put,
    processing,
    errors: formErrors,
    reset,
    transform,
  } = useForm({
    name: "",
    description: "",
    image: null as File | null,
  });

  transform((data) => ({
    client: {
      ...data,
    },
  }));

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setData({
      name: client.name,
      description: client.description,
      image: null,
    });
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setEditingClient(null);
    reset();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingClient) {
      put(`/admin/clients/${editingClient.id}`, {
        onSuccess: () => handleClose(),
      });
    } else {
      post("/admin/clients", {
        onSuccess: () => handleClose(),
      });
    }
  };

  return (
    <DashboardLayout title="Gestión de Clientes" storeLogo={store_logo} storeName={store_name}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Gestión de Clientes
          </h1>
          <p className="text-muted-foreground">Añade y gestiona los clientes destacados</p>
        </div>
        <Button onClick={() => setModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo Cliente
        </Button>
      </div>

      <FormModal
        isOpen={isModalOpen}
        onClose={handleClose}
        title={editingClient ? "Editar Cliente" : "Nuevo Cliente"}
        onSubmit={handleSubmit}
        isProcessing={processing}
      >
        <div className="grid w-full items-center gap-4">
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="name">Nombre</Label>
            <Input
              id="name"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
            />
            {formErrors.name && <div className="text-red-500">{formErrors.name}</div>}
          </div>
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={data.description}
              onChange={(e) => setData("description", e.target.value)}
              rows={3}
            />
            {formErrors.description && <div className="text-red-500">{formErrors.description}</div>}
          </div>
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="image">Imagen</Label>
            <Input
              id="image"
              type="file"
              accept="image/*"
              onChange={(e) =>
                setData("image", e.target.files ? e.target.files[0] : null)
              }
            />
            {formErrors.image && <div className="text-red-500">{formErrors.image}</div>}
          </div>
        </div>
      </FormModal>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {clients.map((client) => (
          <Card key={client.id}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {client.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                {client.description}
              </p>
              <div className="flex justify-self-center mb-4">
                {client.image_url ? (
                  <img
                    src={client.image_url}
                    alt={client.name}
                    className="h-20 w-20 object-cover rounded-lg"
                  />
                ) : (
                  <ImageIcon className="h-20 w-20 text-muted-foreground" />
                )}
              </div>
              <div className="flex items-center justify-end">
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(client)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      confirm(`¿Estás seguro de que quieres eliminar el cliente "${client.name}"? Esta acción no se puede deshacer.`) &&
                      destroy(`/admin/clients/${client.id}`, { method: "delete" })
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {clients.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10">
            <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No hay clientes</h3>
            <p className="text-muted-foreground text-center mb-4">
              Comienza agregando tu primer cliente destacado.
            </p>
            <Button onClick={() => setModalOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Crear Primer Cliente
            </Button>
          </CardContent>
        </Card>
      )}
    </DashboardLayout>
  );
};

export default Clients;